/**
 * Premium Search Hook
 * Combines hybrid search with analytics, filters, and smart features
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { buildSearchIndex, SearchItem, popularSearches } from '@/utils/searchIndex';
import { 
  hybridSearch, 
  SearchResult, 
  SearchFilters, 
  SynonymMap,
  normalizeQuery,
  getContentTypeIcon 
} from '@/utils/hybridSearch';
import { useDebounce } from './useDebounce';
import type { Json } from '@/integrations/supabase/types';

interface PinnedResult {
  keyword: string;
  url: string;
  title: string;
  description?: string;
  type: string;
  priority: number;
}

interface SearchAnalyticsInsert {
  session_id: string;
  query: string;
  query_normalized: string;
  result_count: number;
  was_no_results: boolean;
  search_type: 'lexical' | 'semantic' | 'hybrid';
  search_duration_ms: number;
  filters_used: Json;
  user_id?: string;
  clicked_result_id?: string;
  clicked_result_url?: string;
  clicked_result_type?: string;
  click_position?: number;
  time_to_click_ms?: number;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  const key = 'dialgia_search_session';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

export const usePremiumSearch = () => {
  const [searchIndex, setSearchIndex] = useState<SearchItem[]>([]);
  const [synonymMap, setSynonymMap] = useState<SynonymMap>({});
  const [pinnedResults, setPinnedResults] = useState<PinnedResult[]>([]);
  const [isIndexReady, setIsIndexReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  const debouncedQuery = useDebounce(query, 200);
  const searchStartTime = useRef<number>(0);
  const sessionId = useRef(getSessionId());

  // Build search index on mount
  useEffect(() => {
    const initializeSearch = async () => {
      setIsLoading(true);
      try {
        // Build local search index
        await new Promise(resolve => setTimeout(resolve, 10)); // Prevent UI blocking
        const index = buildSearchIndex();
        setSearchIndex(index);

        // Load synonyms from database
        const { data: synonymsData } = await supabase
          .from('search_synonyms')
          .select('term, synonyms, weight')
          .eq('is_active', true);

        if (synonymsData) {
          const map: SynonymMap = {};
          synonymsData.forEach(s => {
            map[s.term] = { 
              synonyms: s.synonyms as string[], 
              weight: Number(s.weight) || 0.8 
            };
          });
          setSynonymMap(map);
        }

        // Load pinned results
        const { data: pinnedData } = await supabase
          .from('search_pinned_results')
          .select('keyword, result_url, result_title, result_description, result_type, priority')
          .eq('is_active', true);

        if (pinnedData) {
          setPinnedResults(pinnedData.map(p => ({
            keyword: p.keyword,
            url: p.result_url,
            title: p.result_title,
            description: p.result_description || undefined,
            type: p.result_type,
            priority: p.priority || 0,
          })));
        }

        setIsIndexReady(true);
      } catch (error) {
        console.error('Failed to initialize search:', error);
        // Still mark as ready with local index
        setIsIndexReady(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSearch();
  }, []);

  // Perform search
  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim() || !isIndexReady) return [];
    
    searchStartTime.current = performance.now();
    
    const results = hybridSearch(debouncedQuery, searchIndex, {
      synonymMap,
      filters: {
        contentType: selectedCategory !== 'all' ? [selectedCategory] : undefined,
        ...filters,
      },
      pinnedResults,
      typoTolerance: true,
      limit: 25,
    });

    return results;
  }, [debouncedQuery, searchIndex, synonymMap, pinnedResults, filters, selectedCategory, isIndexReady]);

  // Track search analytics
  useEffect(() => {
    if (!debouncedQuery.trim() || !isIndexReady) return;

    const trackSearch = async () => {
      const duration = performance.now() - searchStartTime.current;
      
      const analyticsData: SearchAnalyticsInsert = {
        session_id: sessionId.current,
        query: debouncedQuery,
        query_normalized: normalizeQuery(debouncedQuery),
        result_count: searchResults.length,
        was_no_results: searchResults.length === 0,
        search_type: 'hybrid',
        search_duration_ms: Math.round(duration),
        filters_used: JSON.parse(JSON.stringify({
          contentType: selectedCategory !== 'all' ? [selectedCategory] : undefined,
          ...filters,
        })) as Json,
      };

      try {
        await supabase.from('search_analytics').insert([analyticsData]);
      } catch (error) {
        // Silently fail - analytics shouldn't break search
        console.debug('Search analytics tracking failed:', error);
      }
    };

    // Debounce analytics tracking
    const timer = setTimeout(trackSearch, 500);
    return () => clearTimeout(timer);
  }, [debouncedQuery, searchResults.length, selectedCategory, filters, isIndexReady]);

  // Track click on result
  const trackResultClick = useCallback(async (result: SearchResult, position: number) => {
    const clickTime = performance.now() - searchStartTime.current;

    try {
      await supabase.from('search_analytics').insert([{
        session_id: sessionId.current,
        query: debouncedQuery,
        query_normalized: normalizeQuery(debouncedQuery),
        result_count: searchResults.length,
        clicked_result_id: result.id,
        clicked_result_url: result.url,
        clicked_result_type: result.type,
        click_position: position,
        time_to_click_ms: Math.round(clickTime),
        was_no_results: false,
        search_type: 'hybrid',
        search_duration_ms: 0,
        filters_used: {},
      }]);
    } catch (error) {
      console.debug('Click tracking failed:', error);
    }
  }, [debouncedQuery, searchResults.length]);

  // Get instant suggestions (faster than full search).
  // Each suggestion carries its source item when one exists so clicking a
  // suggestion navigates to the EXACT item the label represents, instead of
  // re-running a fresh hybrid search that may surface different top results.
  const suggestions = useMemo<Array<{ label: string; item?: SearchItem }>>(() => {
    if (!query.trim() || query.length < 2 || !isIndexReady) return [];

    const normalizedQuery = query.toLowerCase();
    const seen = new Set<string>();
    const out: Array<{ label: string; item?: SearchItem }> = [];
    const push = (label: string, item?: SearchItem) => {
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ label, item });
    };

    // Titles that START with the query (highest intent)
    searchIndex
      .filter(item => item.title.toLowerCase().startsWith(normalizedQuery))
      .slice(0, 4)
      .forEach(item => push(item.title, item));

    // Titles that CONTAIN the query
    searchIndex
      .filter(item =>
        item.title.toLowerCase().includes(normalizedQuery) &&
        !item.title.toLowerCase().startsWith(normalizedQuery)
      )
      .slice(0, 4)
      .forEach(item => push(item.title, item));

    // Matching keywords as a fallback (no bound item — these stay as query refinements)
    for (const item of searchIndex) {
      for (const keyword of item.keywords) {
        if (out.length >= 8) break;
        if (keyword.toLowerCase().includes(normalizedQuery)) {
          push(keyword.charAt(0).toUpperCase() + keyword.slice(1));
        }
      }
      if (out.length >= 8) break;
    }

    return out.slice(0, 8);
  }, [query, searchIndex, isIndexReady]);

  // Recent searches from localStorage
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  useEffect(() => {
    try {
      const stored = localStorage.getItem('dialgia_recent_searches');
      setRecentSearches(stored ? JSON.parse(stored) : []);
    } catch {
      setRecentSearches([]);
    }
  }, []);

  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    try {
      const recent = [...recentSearches];
      const updated = [searchQuery, ...recent.filter(s => s !== searchQuery)].slice(0, 5);
      localStorage.setItem('dialgia_recent_searches', JSON.stringify(updated));
      setRecentSearches(updated);
    } catch {
      // Ignore localStorage errors
    }
  }, [recentSearches]);

  // Handle search submission
  const handleSearch = useCallback((searchQuery: string) => {
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery);
      setQuery(searchQuery);
    }
  }, [saveRecentSearch]);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setSelectedCategory('all');
    setFilters({});
  }, []);

  // Search categories for filtering
  const searchCategories = useMemo(() => [
    { id: 'all', label: 'All Results', count: searchResults.length, icon: '🔍' },
    { id: 'product', label: 'Products', count: searchResults.filter(r => r.type === 'product').length, icon: '📦' },
    { id: 'page', label: 'Pages', count: searchResults.filter(r => r.type === 'page').length, icon: '📄' },
    { id: 'feature', label: 'Features', count: searchResults.filter(r => r.type === 'feature').length, icon: '⚡' },
    { id: 'category', label: 'Categories', count: searchResults.filter(r => r.type === 'category').length, icon: '📁' },
  ], [searchResults]);

  return {
    // State
    query,
    setQuery,
    filters,
    setFilters,
    selectedCategory,
    setSelectedCategory,
    isSearching: isLoading || !isIndexReady,
    isIndexReady,
    
    // Results
    searchResults,
    suggestions,
    recentSearches,
    popularSearches,
    searchCategories,
    
    // Actions
    handleSearch,
    clearSearch,
    trackResultClick,
    
    // Utilities
    getContentTypeIcon,
    
    // Stats
    totalIndexedItems: searchIndex.length,
    hasSynonyms: Object.keys(synonymMap).length > 0,
  };
};
